import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Award, Trophy, Target } from "lucide-react";
import api from "../../api";
import { useLang } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { useConfig } from "../../context/ConfigContext";
import MagicalCard from "../../components/MagicalCard";
import Avatar from "../../components/Avatar";
import LevelPath from "../../components/LevelPath";
import { Icon, EmptyState, SkeletonPanel } from "../../components/common";
import { fmtRelative, fmtDate } from "../../utils";

export default function ProfilePage() {
  const { t, pick, lang } = useLang();
  const { user } = useAuth();
  const { config } = useConfig();
  const { id } = useParams();
  const sid = id || user?.id;
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!sid) return;
    setData(null);
    api.get(`/profile/${sid}`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/points/history", { params: { student_id: sid } }).then((r) => setHistory(r.data.transactions)).catch(() => {});
  }, [sid]);

  const chartData = useMemo(() => {
    const sorted = [...history].filter((t2) => t2.status === "active")
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    let acc = 0;
    const byDay = {};
    for (const tx of sorted) {
      acc += tx.amount;
      byDay[new Date(tx.created_at).toISOString().slice(0, 10)] = acc;
    }
    return Object.entries(byDay).map(([date, points]) => ({ date: date.slice(5), points }));
  }, [history]);

  if (data === false) return <EmptyState icon="ShieldAlert" title={t("common.error_generic")} testId="profile-error" />;
  if (!data) return <SkeletonPanel lines={6} />;
  const s = data.student;
  const cur = data.level.current;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="profile-page">
      <MagicalCard hover={false} className="!pt-6">
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <Avatar src={s.avatar} name={pick(s, "name")} size="xl" crown />
          <div className="text-center sm:text-start flex-1">
            <h2 data-testid="profile-page-name" className="text-3xl font-black">{pick(s, "name")}</h2>
            <p className="text-sm text-[#A5A0D6] font-bold">{lang === "ar" ? s.class_name_ar : (s.class_name_en || s.class_name_ar)}</p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
              <span className="px-3 py-1.5 rounded-full text-xs font-extrabold border" style={{ color: cur?.color, borderColor: `${cur?.color}66`, background: `${cur?.color}1A` }}>
                {cur ? pick(cur, "name") : "—"}
              </span>
              <span className="px-3 py-1.5 rounded-full text-xs font-extrabold bg-[#27C9EE]/12 border border-[#27C9EE]/40 text-[#27C9EE] flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" /> {t("dash.rank")}: {data.rank ?? "—"}
              </span>
              <span className="px-3 py-1.5 rounded-full text-xs font-extrabold bg-[#FF63B5]/12 border border-[#FF63B5]/40 text-[#FF63B5] flex items-center gap-1">
                <Award className="w-3.5 h-3.5" /> {data.badges.length} {t("dash.badges_count")}
              </span>
              <span className="px-3 py-1.5 rounded-full text-xs font-extrabold bg-[#48D276]/12 border border-[#48D276]/40 text-[#48D276] flex items-center gap-1">
                <Target className="w-3.5 h-3.5" /> {data.challenges_completed} {t("nav.challenges")}
              </span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-4xl font-black text-[#48D276]" data-testid="profile-page-points">{data.total_points}</p>
            <p className="text-xs font-bold text-[#A5A0D6]">{t("dash.points_label")}</p>
            {data.level.next && (
              <p className="text-xs font-extrabold text-[#FFC83D] mt-1">
                {t("dash.remaining_to_level", { n: data.level.remaining, level: pick(data.level.next, "name") })}
              </p>
            )}
          </div>
        </div>
        <div className="progress-track h-4 mt-5">
          <div className="progress-fill h-full" style={{ width: `${data.level.progress}%` }} />
        </div>
      </MagicalCard>

      <MagicalCard title={t("dash.excellence_stages")}>
        <LevelPath levels={config?.levels || []} currentPoints={data.total_points} />
      </MagicalCard>

      {chartData.length > 1 && (
        <MagicalCard title={t("history.title")} variant="ribbon-cyan" testId="progress-chart-card">
          <div dir="ltr" className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#9C42F5" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#9C42F5" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#A5A0D6", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#A5A0D6", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#171347", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff" }} />
                <Area type="monotone" dataKey="points" stroke="#9C42F5" strokeWidth={3} fill="url(#pg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </MagicalCard>
      )}

      <MagicalCard title={t("dash.badges")} variant="ribbon-pink">
        {data.badges.length === 0 ? (
          <EmptyState icon="Award" title={t("badges_page.empty")} testId="profile-badges-empty" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.badges.map((b) => (
              <div key={b.key} data-testid={`profile-badge-${b.key}`} className="rounded-2xl p-3 border text-center"
                style={{ background: `linear-gradient(165deg, ${b.color}26, ${b.color}08)`, borderColor: `${b.color}44` }}>
                <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-1.5"
                  style={{ background: `linear-gradient(135deg, ${b.color}, ${b.color}99)`, boxShadow: `0 0 14px ${b.color}55` }}>
                  <Icon name={b.icon} className="w-6 h-6 text-white" />
                </div>
                <p className="text-xs font-extrabold">{lang === "ar" ? b.name_ar : b.name_en}</p>
                <p className="text-[10px] text-[#A5A0D6] font-bold mt-0.5">{fmtDate(b.awarded_at, lang)}</p>
              </div>
            ))}
          </div>
        )}
      </MagicalCard>

      <MagicalCard title={t("teacher.recent_activity")} variant="ribbon-cyan">
        {data.recent.length === 0 ? (
          <EmptyState icon="History" title={t("history.empty")} testId="profile-recent-empty" />
        ) : (
          <ul className="space-y-2">
            {data.recent.map((tx) => (
              <li key={tx.id} className={`flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/8 px-3 py-2.5 ${tx.status === "reversed" ? "opacity-50" : ""}`}>
                <span className={`text-lg font-black ${tx.amount > 0 ? "text-[#48D276]" : "text-[#FF8FB1]"}`} dir="ltr">
                  {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                </span>
                <span className="flex-1 text-sm font-bold truncate" style={{ color: tx.category_color }}>
                  {lang === "ar" ? tx.category_name_ar : tx.category_name_en}
                </span>
                <span className="text-xs text-[#A5A0D6] font-bold">{fmtRelative(tx.created_at, lang)}</span>
              </li>
            ))}
          </ul>
        )}
      </MagicalCard>
    </div>
  );
}
